import { Dialog, Switch, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { FormTemplateScope, FormType, type FormTemplateCreatePayload } from '@/types'

interface TemplateCreateModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: FormTemplateCreatePayload) => Promise<void>
  submitting?: boolean
}

const scopeOptions: Array<{ label: string; value: FormTemplateScope }> = [
  { label: '組織模板', value: FormTemplateScope.ORGANIZATION },
  { label: '專案模板', value: FormTemplateScope.PROJECT },
  { label: '個人模板', value: FormTemplateScope.PERSONAL },
]

export function TemplateCreateModal({ open, onClose, onSubmit, submitting = false }: TemplateCreateModalProps) {
  const [formState, setFormState] = useState<FormTemplateCreatePayload>({
    name: '',
    scopeType: FormTemplateScope.ORGANIZATION,
    formType: FormType.ONE_OFF,
    description: '',
    projectId: undefined,
    activationStartDate: '',
    activationEndDate: '',
    isAlwaysActive: false,
  })
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!formState.name.trim()) {
      setError('請輸入模板名稱')
      return
    }

    if (formState.scopeType === FormTemplateScope.PROJECT && !formState.projectId?.trim()) {
      setError('專案模板需要提供 projectId')
      return
    }

    try {
      const activationStartDate = formState.isAlwaysActive
        ? null
        : formState.activationStartDate
          ? formState.activationStartDate.trim() || null
          : null
      const activationEndDate = formState.isAlwaysActive
        ? null
        : formState.activationEndDate
          ? formState.activationEndDate.trim() || null
          : null

      const payload: FormTemplateCreatePayload = {
        name: formState.name.trim(),
        description: formState.description?.trim() || undefined,
        scopeType: formState.scopeType,
        formType: formState.formType,
        projectId:
          formState.scopeType === FormTemplateScope.PROJECT
            ? formState.projectId?.trim() || undefined
            : undefined,
        activationStartDate,
        activationEndDate,
        isAlwaysActive: formState.isAlwaysActive ?? false,
      }

      await onSubmit(payload)
      setFormState({
        name: '',
        scopeType: FormTemplateScope.ORGANIZATION,
        formType: FormType.ONE_OFF,
        description: '',
        projectId: undefined,
        activationStartDate: '',
        activationEndDate: '',
        isAlwaysActive: false,
      })
      setError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : '建立模板失敗'
      setError(message)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setError(null)
    setFormState({
      name: '',
      scopeType: FormTemplateScope.ORGANIZATION,
      formType: FormType.ONE_OFF,
      description: '',
      projectId: undefined,
      activationStartDate: '',
      activationEndDate: '',
      isAlwaysActive: false,
    })
    onClose()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <Dialog.Title className="text-lg font-semibold text-gray-900">新增表單模板</Dialog.Title>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">模板名稱</label>
                    <input
                      type="text"
                      value={formState.name}
                      onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="例如：消防設備定檢表"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">範圍</label>
                    <select
                      value={formState.scopeType}
                      onChange={(e) =>
                        setFormState(prev => ({
                          ...prev,
                          scopeType: e.target.value as FormTemplateScope,
                          projectId: e.target.value === FormTemplateScope.PROJECT ? prev.projectId : undefined,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      {scopeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">表單型態</label>
                    <select
                      value={formState.formType ?? FormType.ONE_OFF}
                      onChange={(e) =>
                        setFormState(prev => ({
                          ...prev,
                          formType: e.target.value as FormType,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value={FormType.ONE_OFF}>一次性表單</option>
                      <option value={FormType.PERIODIC}>定期檢查表</option>
                    </select>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">啟用開始日</label>
                      <input
                        type="date"
                        value={formState.activationStartDate ?? ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, activationStartDate: e.target.value }))}
                        disabled={Boolean(formState.isAlwaysActive)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">啟用結束日</label>
                      <input
                        type="date"
                        value={formState.activationEndDate ?? ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, activationEndDate: e.target.value }))}
                        disabled={Boolean(formState.isAlwaysActive)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">長駐啟用</p>
                      <p className="text-xs text-gray-500">若啟用，表單將長期顯示並忽略日期設定。</p>
                    </div>
                    <Switch
                      checked={Boolean(formState.isAlwaysActive)}
                      onChange={(value: boolean) => setFormState(prev => ({
                        ...prev,
                        isAlwaysActive: value,
                        activationStartDate: value ? '' : prev.activationStartDate,
                        activationEndDate: value ? '' : prev.activationEndDate,
                      }))}
                      className={`${formState.isAlwaysActive ? 'bg-primary-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${formState.isAlwaysActive ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                      />
                    </Switch>
                  </div>

                  {formState.scopeType === FormTemplateScope.PROJECT && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">專案 ID</label>
                      <input
                        type="text"
                        value={formState.projectId ?? ''}
                        onChange={(e) => setFormState(prev => ({ ...prev, projectId: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        placeholder="輸入專案識別碼"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">描述（選填）</label>
                    <textarea
                      value={formState.description ?? ''}
                      onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="補充使用情境與規範"
                    />
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                    disabled={submitting}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-70"
                    disabled={submitting}
                  >
                    {submitting ? '建立中…' : '建立模板'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default TemplateCreateModal
